/**
 * EquipmentDetailsSection Component
 * Column 1 of the reference expanded-row layout.
 * NO outer card — renders directly on the row zebra background.
 *
 * Layout:
 *   - Gray pill header: "Equipment details"
 *   - Label/value rows: Load ID, Commodity, Length, Weight, Load, Truck Type, Reference ID
 *   - Comments section at the bottom (if present)
 */

import React, { useCallback, useState } from 'react'
import { CanonicalLoad, stripExternalIdPrefix } from '@numeo/types-core'
import { Tooltip } from '@mui/material'
import { cn } from 'utils/cn'
import { formatEasternDateTime } from 'utils/format-date'

interface EquipmentDetailsSectionProps {
    load: CanonicalLoad
}

const CopyIcon: React.FC<{ copied: boolean }> = ({ copied }) =>
    copied ? (
        <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-[var(--lh-success)]"
        >
            <polyline points="20 6 9 17 4 12" />
        </svg>
    ) : (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
        </svg>
    )

export const EquipmentDetailsSection: React.FC<EquipmentDetailsSectionProps> = ({ load }) => {
    const [copiedField, setCopiedField] = useState<string | null>(null)

    const handleCopy = useCallback(async (text: string, field: string) => {
        try {
            await navigator.clipboard.writeText(text)
            setCopiedField(field)
            setTimeout(() => setCopiedField(null), 1500)
        } catch {
            // silently fail
        }
    }, [])

    // Convoy: `loadID` is the friendly `convoyId` ("29722999") — short and copy-able. The
    // long opaque S-token lives in `externalId` and is only used for the deep-link URL (it's
    // useless to a dispatcher reading the row), so we hide the Reference ID row for Convoy
    // to keep the panel tidy.
    const isConvoy = load.sourceType === 'CONVOY'
    // DAT / DAT_EXTENSION: loadID is the opaque postingId — not useful to dispatchers.
    // Reference ID comes from postersReferenceId (broker's own reference, e.g. "SW188").
    const isDat = load.sourceType === 'DAT' || load.sourceType === 'DAT_EXTENSION'
    // externalId is the internal SOURCE|rawId dedup key. It must remain composite
    // for client-side matching, but the UI only presents the source's raw load ID.
    const displayLoadId = load.loadID?.toString() || stripExternalIdPrefix(load.externalId)

    // Sylectus is the only source that carries a precise expiry — surface it near the top (row 2).
    const sylectusExpiry = load.sourceType === 'SYLECTUS' ? formatEasternDateTime(load.expirationAt, { withYear: true, withZone: true }) : null

    const rows: Array<{ label: string; value: string; copy?: boolean; accent?: boolean }> = []
    if (!isDat) {
        rows.push({
            label: isConvoy ? 'Convoy ID' : 'Load ID',
            value: displayLoadId || '—',
            copy: Boolean(displayLoadId),
        })
    }
    if (sylectusExpiry) rows.push({ label: 'Expires', value: sylectusExpiry, accent: true })
    rows.push(
        { label: 'Commodity', value: load.commodity || '—' },
        { label: 'Length', value: load.equipmentLengthFt ? `${load.equipmentLengthFt} ft` : '—' },
        { label: 'Weight', value: load.weightLbs ? `${load.weightLbs.toLocaleString()} lbs` : '—' }
    )
    if (load.fullPartial) rows.push({ label: 'Load', value: load.fullPartial })
    if (load.equipmentType) rows.push({ label: 'Truck Type', value: load.equipmentType })
    if (isDat) {
        if (load.postersReferenceId) rows.push({ label: 'Reference ID', value: load.postersReferenceId, copy: true })
    } else if (displayLoadId && !isConvoy) {
        rows.push({ label: 'Reference ID', value: displayLoadId, copy: true })
    }

    return (
        <div className="w-full h-full min-w-0 flex flex-col gap-1 overflow-y-auto">
            {/* Gray pill header — reference line 8218-8236 */}
            <div className="h-7 min-h-[28px] flex items-center px-2 rounded-lg bg-[var(--lh-surface-secondary)] text-[12px] font-medium text-[var(--lh-fg)] mb-1 shrink-0">
                Equipment details
            </div>

            {/* Label/value rows */}
            {rows.map((r) => (
                <div key={r.label} className="flex justify-between items-center py-[3px] text-[12px]">
                    <span className="text-[var(--lh-fg-muted)]">{r.label}</span>
                    <span className="flex items-center gap-1.5">
                        <span
                            className={cn(
                                'font-semibold',
                                r.accent ? 'text-[var(--lh-accent)]' : 'text-[var(--lh-fg)]',
                                r.copy && 'cursor-pointer hover:opacity-80'
                            )}
                            onClick={r.copy ? () => handleCopy(r.value, r.label) : undefined}
                        >
                            {r.value}
                        </span>
                        {r.copy && (
                            <Tooltip title={copiedField === r.label ? 'Copied!' : 'Copy'} arrow placement="top">
                                <button
                                    onClick={() => handleCopy(r.value, r.label)}
                                    className="w-5 h-5 flex items-center justify-center border-none bg-transparent cursor-pointer text-[var(--lh-fg-muted)] hover:text-[var(--lh-fg)] transition-colors"
                                >
                                    <CopyIcon copied={copiedField === r.label} />
                                </button>
                            </Tooltip>
                        )}
                    </span>
                </div>
            ))}

            {/* Numeo Load the carrier doesn't currently qualify for: comment/specialRequirements
             * are already redacted server-side (loads-ingest-service's
             * redactUnmatchedBrokerPortalLoad), so this note replaces both rows instead of
             * silently showing nothing — a broker did add notes, they're just gated. */}
            {load.sourceType === 'BROKER_PORTAL' && load.eligibilityMatched === false && (
                <div className="pt-2.5 flex flex-col gap-1.5">
                    <span className="text-[12px] text-[var(--lh-warning)]">Comments and requirements are hidden until you verify your MC with Numeo.</span>
                </div>
            )}

            {/* Comments */}
            {load.comment && (
                <div className="pt-2.5 flex flex-col gap-1.5">
                    <span className="text-[12px] text-[var(--lh-fg-muted)]">Comments</span>
                    <span className="text-[12px] font-semibold text-[var(--lh-fg)] leading-[1.4] whitespace-pre-line">{load.comment}</span>
                </div>
            )}

            {load.specialRequirements && load.sourceType !== 'BEON' && (
                <div className="pt-2.5 flex flex-col gap-1.5">
                    <span className="text-[12px] text-[var(--lh-fg-muted)]">Requirements</span>
                    <span className="text-[12px] font-semibold text-[var(--lh-fg)] leading-[1.4] whitespace-pre-line">{load.specialRequirements}</span>
                </div>
            )}
        </div>
    )
}
